import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';

import type { Suggestion } from '../aiSuggestionsPluginType';

const hoverHighlightStyle = convertToInlineCss({
	backgroundColor: token('color.background.accent.blue.subtler'),
	// Extend the highlight evenly around the node via a same-colour box-shadow
	// spread instead of padding/margin — box-shadow takes no layout space, so the
	// text doesn't shift while the background still has consistent padding.
	boxShadow: `0 0 0 ${token('space.025')} ${token('color.background.accent.blue.subtler')}`,
});

/**
 * CSS class names for node types that need a different hover treatment than the
 * default inline box-shadow highlight (which suits text blocks and cards). The
 * matching styles live in `HoverHighlightGlobalStyles` and mirror how the block
 * controls "remix" selection treats these nodes.
 */
export const hoverHighlightClassNames = {
	overlay: 'ai-suggestions-hover-overlay',
	table: 'ai-suggestions-hover-table',
	extension: 'ai-suggestions-hover-extension',
	rule: 'ai-suggestions-hover-rule',
};

// Container nodes are full-bleed and/or have their own borders, so a box-shadow
// "padding" looks wrong. They get a translucent blanket overlay instead.
const overlayHoverNodeTypes = new Set([
	'panel',
	'codeBlock',
	'expand',
	'nestedExpand',
	'layoutSection',
]);
const extensionHoverNodeTypes = new Set(['extension', 'bodiedExtension', 'multiBodiedExtension']);
// Media is handled by the media plugin's own selection affordance — skip it.
const excludedHoverNodeTypes = new Set(['media', 'mediaSingle', 'mediaGroup']);

/**
 * Builds the blue highlight decorations for every node targeted by the hovered
 * suggestion's tool calls. Inline targets get a `Decoration.inline`; block
 * targets get a `Decoration.node`. Returns `DecorationSet.empty` when there is
 * nothing to highlight so the `decorations` prop can cheaply skip merging.
 */
export function calculateHoverDecorations(
	doc: PMNode,
	suggestion: Suggestion | undefined,
): DecorationSet {
	if (!suggestion) {
		return DecorationSet.empty;
	}

	const targetLocalIds = new Set<string>();
	for (const toolCall of suggestion.toolCalls) {
		if (toolCall.name === 'removeNodes') {
			toolCall.arguments.localIds.forEach((id) => targetLocalIds.add(id));
		} else if ('localId' in toolCall.arguments) {
			targetLocalIds.add(toolCall.arguments.localId);
		}
	}

	if (targetLocalIds.size === 0) {
		return DecorationSet.empty;
	}

	const decorations: Decoration[] = [];
	const matchedLocalIds = new Set<string>();
	doc.descendants((node, pos) => {
		const { localId } = node.attrs ?? {};
		if (localId && targetLocalIds.has(localId)) {
			matchedLocalIds.add(localId);
			const typeName = node.type.name;
			const to = pos + node.nodeSize;
			if (excludedHoverNodeTypes.has(typeName)) {
				// no highlight
			} else if (node.isInline) {
				decorations.push(Decoration.inline(pos, to, { style: hoverHighlightStyle }));
			} else if (typeName === 'table') {
				decorations.push(Decoration.node(pos, to, { class: hoverHighlightClassNames.table }));
			} else if (overlayHoverNodeTypes.has(typeName)) {
				decorations.push(Decoration.node(pos, to, { class: hoverHighlightClassNames.overlay }));
			} else if (extensionHoverNodeTypes.has(typeName)) {
				decorations.push(Decoration.node(pos, to, { class: hoverHighlightClassNames.extension }));
			} else if (typeName === 'rule') {
				decorations.push(Decoration.node(pos, to, { class: hoverHighlightClassNames.rule }));
			} else {
				decorations.push(Decoration.node(pos, to, { style: hoverHighlightStyle }));
			}
		}
		return matchedLocalIds.size < targetLocalIds.size;
	});

	return decorations.length ? DecorationSet.create(doc, decorations) : DecorationSet.empty;
}
